import { useState } from "react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Plus,
  Check,
  Trash,
  CalendarBlank,
  Warning,
  PencilSimple,
  QrCode,
  ListBullets,
  ChartLine,
} from "@phosphor-icons/react";
import {
  AssetMaintenanceSchedule,
  MaintenanceFrequencyLabels,
  MaintenanceType,
  CreateMaintenanceScheduleDto,
  UpdateMaintenanceScheduleDto,
} from "shared/types/asset";
import {
  useAssetMaintenanceSchedules,
  useCreateMaintenanceSchedule,
  useUpdateMaintenanceSchedule,
  useDeleteMaintenanceSchedule,
} from "hooks/useAssets";
import { toast } from "react-toastify";
import {
  MaintenanceForm,
  EMPTY_MAINTENANCE_FORM,
  scheduleToFormData,
} from "./MaintenanceEditDialog";
import type { MaintenanceFormData } from "./MaintenanceEditDialog";
import { MaintenanceValidationDialog } from "./MaintenanceValidationDialog";
import { MaintenanceQRSection } from "./MaintenanceQRSection";
import { MaintenanceTimeline } from "./MaintenanceTimeline";

interface AssetMaintenanceSectionProps {
  assetId: string;
}

export const AssetMaintenanceSection = ({
  assetId,
}: AssetMaintenanceSectionProps) => {
  const { schedules, isLoading, refetch } =
    useAssetMaintenanceSchedules(assetId);
  const { createSchedule, isCreating } = useCreateMaintenanceSchedule();
  const { updateSchedule, isUpdating } = useUpdateMaintenanceSchedule();
  const { deleteSchedule, isDeleting } = useDeleteMaintenanceSchedule();

  const [formMode, setFormMode] = useState<"hidden" | "create" | "edit">(
    "hidden"
  );
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<MaintenanceFormData>(
    EMPTY_MAINTENANCE_FORM
  );
  const [validationSchedule, setValidationSchedule] =
    useState<AssetMaintenanceSchedule | null>(null);
  const [expandedQR, setExpandedQR] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "timeline">("list");

  const openCreateForm = () => {
    setFormData(EMPTY_MAINTENANCE_FORM);
    setEditingId(null);
    setFormMode("create");
  };

  const openEditForm = (schedule: AssetMaintenanceSchedule) => {
    setFormData(scheduleToFormData(schedule));
    setEditingId(schedule.id);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode("hidden");
    setEditingId(null);
    setFormData(EMPTY_MAINTENANCE_FORM);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Naam is verplicht");
      return;
    }

    if (formData.dateMode === "next" && !formData.nextDueDate) {
      toast.error("Volgende onderhoudsdatum is verplicht");
      return;
    }

    if (formData.dateMode === "last" && !formData.lastCompletedDate) {
      toast.error("Laatste onderhoudsdatum is verplicht");
      return;
    }

    try {
      if (formMode === "create") {
        const dto: CreateMaintenanceScheduleDto = {
          name: formData.name,
          frequency: formData.frequency,
          maintenanceType: formData.maintenanceType,
          customFrequencyMonths: formData.customFrequencyMonths,
        };

        if (formData.dateMode === "last") {
          dto.lastCompletedDate = formData.lastCompletedDate;
        } else {
          dto.firstDueDate = formData.nextDueDate;
        }

        await createSchedule(assetId, dto);
        toast.success("Onderhoudsschema aangemaakt");
      } else if (formMode === "edit" && editingId) {
        const dto: UpdateMaintenanceScheduleDto = {
          name: formData.name,
          frequency: formData.frequency,
          maintenanceType: formData.maintenanceType,
          customFrequencyMonths: formData.customFrequencyMonths,
        };

        if (formData.dateMode === "last") {
          dto.lastCompletedDate = formData.lastCompletedDate;
        } else {
          dto.firstDueDate = formData.nextDueDate;
        }

        await updateSchedule(editingId, dto);
        toast.success("Onderhoudsschema bijgewerkt");
      }

      closeForm();
      refetch();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : formMode === "create"
            ? "Kon onderhoudsschema niet aanmaken"
            : "Kon onderhoudsschema niet bijwerken";
      toast.error(message);
      console.error("Error saving maintenance schedule:", error);
    }
  };

  const handleDelete = async (scheduleId: number) => {
    try {
      await deleteSchedule(scheduleId);
      toast.success("Onderhoudsschema verwijderd");
      refetch();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon schema niet verwijderen";
      toast.error(message);
      console.error("Error deleting maintenance schedule:", error);
    }
  };

  const isOverdue = (schedule: AssetMaintenanceSchedule) => {
    if (!schedule.nextDueDate) return false;
    return new Date(schedule.nextDueDate) < new Date();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("nl-BE");
  };

  const isSaving = isCreating || isUpdating;

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Laden...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Onderhoud</h4>
        <div className="flex items-center gap-1">
          {schedules.length > 0 && formMode === "hidden" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setViewMode(viewMode === "list" ? "timeline" : "list")
              }
              title={
                viewMode === "list"
                  ? "Toon tijdlijn"
                  : "Toon lijst"
              }
              className="h-7 w-7 p-0"
            >
              {viewMode === "list" ? (
                <ChartLine className="h-3.5 w-3.5" />
              ) : (
                <ListBullets className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          {formMode === "hidden" && viewMode === "list" && (
            <Button variant="ghost" size="sm" onClick={openCreateForm}>
              <Plus className="mr-1 h-3 w-3" />
              Toevoegen
            </Button>
          )}
        </div>
      </div>

      {/* Create / Edit form */}
      {formMode !== "hidden" && (
        <MaintenanceForm
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          onCancel={closeForm}
          isSaving={isSaving}
          isEdit={formMode === "edit"}
        />
      )}

      {viewMode === "timeline" ? (
        <MaintenanceTimeline
          schedules={schedules}
          onRefetchSchedules={refetch}
        />
      ) : (
        <>
          {schedules.length === 0 && formMode === "hidden" && (
            <p className="text-xs text-muted-foreground">
              Geen onderhoudsschema's gepland
            </p>
          )}

          <div className="space-y-2">
            {schedules.map((schedule) => (
              <div key={schedule.id} className="rounded-lg border">
                <div
                  className={`flex items-center justify-between p-2 ${
                    editingId === schedule.id ? "border-primary/50 bg-primary/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <CalendarBlank className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">
                          {schedule.name}
                        </p>
                        <MaintenanceTypeBadge type={schedule.maintenanceType} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {MaintenanceFrequencyLabels[schedule.frequency]}
                        {schedule.nextDueDate &&
                          ` - Volgende: ${formatDate(schedule.nextDueDate)}`}
                      </p>
                    </div>
                    {isOverdue(schedule) && (
                      <Badge variant="destructive" className="text-xs shrink-0">
                        <Warning className="mr-1 h-3 w-3" />
                        Achterstallig
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {schedule.publicToken && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setExpandedQR(expandedQR === schedule.id ? null : schedule.id)
                        }
                        className={expandedQR === schedule.id ? "text-primary" : ""}
                        title="QR code voor leverancier"
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditForm(schedule)}
                      disabled={formMode === "edit" && editingId === schedule.id}
                      title="Bewerk schema"
                    >
                      <PencilSimple className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setValidationSchedule(schedule)}
                      title="Valideer onderhoud"
                    >
                      <Check className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(schedule.id)}
                      disabled={isDeleting}
                      title="Verwijder schema"
                    >
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                {expandedQR === schedule.id && schedule.publicToken && (
                  <div className="border-t px-2 pb-2 pt-2">
                    <MaintenanceQRSection
                      publicToken={schedule.publicToken}
                      scheduleName={schedule.name}
                      compact
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Validation Dialog */}
      <MaintenanceValidationDialog
        schedule={validationSchedule}
        onClose={() => setValidationSchedule(null)}
        onSuccess={refetch}
      />
    </div>
  );
};

function MaintenanceTypeBadge({ type }: { type: MaintenanceType }) {
  const isCritical = type === MaintenanceType.CRITICAL;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
        isCritical ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isCritical ? "bg-red-500" : "bg-green-500"
        }`}
      />
      {isCritical ? "Kritiek" : "Preventief"}
    </span>
  );
}
